import { userFullNameBuilder } from '../../../utils/userFullNameBuilder';

/**
 * Notification Feed v2 document -> everything the dropdown row needs to render.
 *
 * Pure on purpose: no DOM, no store, no timezone formatting. The component still
 * owns the avatar fallback (generateCustomAvatar touches `document`) and the
 * timestamp formatting (needs the viewer's timezone at render time).
 *
 * Rows are laid out as a bold title line above a body line, which is what the
 * socket implementation did. The title is normally the sender's name; `Reminder`
 * is the one row that is not from a person.
 */

/** `notificationMessage.sendReminder` on the backend. Reminders are not sent by
 * a person, so the title line names the system instead of a sender. */
const REMINDER_TITLE = 'iMentor Reminder';

export const NOTIFICATION_ICON_REMINDER = 'reminder';
export const NOTIFICATION_ICON_AVATAR = 'avatar';

/**
 * Subcategories the dropdown renders, and where clicking one goes.
 *
 * Absent by design: the whole `Message` category (chat has its own unread UI)
 * and the `Prompt` category (no agreed design yet) — the backend excludes both
 * from the feed, so they never arrive here. Anything unrecognised is skipped
 * rather than guessed at; see formatNotificationFeedItem's return of null.
 */
const ROUTES = {
  Matched: null, // role-dependent, resolved below
  EventApproval: '/events',
  EventApproved: '/events',
  EventDecline: '/events',
  InvitationApproved: '/events',
  InvitationDeclined: '/events',
  EventInvited: '/events',
  Reminder: '/home',
  ContentAssigned: '/content',
};

const resolveRoute = (subcategory, role) => {
  if (subcategory === 'Matched') {
    return role === 'Mentor' ? '/mentees' : '/mentors';
  }

  return ROUTES[subcategory] ?? null;
};

/** The invited-guest row opens the Events page on its invitations tab. */
const resolveRouteState = subcategory => (subcategory === 'EventInvited' ? { currentTab: 1 } : undefined);

/**
 * `EventApproval` is the one subcategory whose backend content already opens
 * with the sender's full name, so rendering the name as the title line too would
 * repeat it. Both strings are built from the same snapshot fields by the same
 * builder, so an exact prefix match is reliable; when it does not match we leave
 * the content untouched rather than cutting at a guess.
 */
const stripLeadingName = (content, fullName) => {
  if (!content || !fullName || fullName === '--') {
    return content;
  }

  return content.startsWith(`${fullName} `) ? content.slice(fullName.length + 1) : content;
};

/**
 * v2 carries the event name inside `content` already (for example
 * `"Please RSVP: Spring Social"`), unlike the legacy payload where the FE had to
 * append `event.event_name`. Appending it here would duplicate it.
 */
const resolveBody = (doc, fullName) =>
  doc.subcategory === 'EventApproval' ? stripLeadingName(doc.content, fullName) : doc.content;

/**
 * The legacy list endpoint computed an `Ended`/`Active` status server-side; the
 * v2 snapshot carries `endDate` instead, so the same question is answered here.
 */
const isEventEnded = (endDate, now) => {
  if (!endDate) {
    return false;
  }

  const end = new Date(endDate).getTime();

  return Number.isNaN(end) ? false : end <= now;
};

const buildEvent = (doc, now) => {
  if (!doc.event) {
    return null;
  }

  return {
    eventId: doc.event.eventId,
    name: doc.event.name,
    startDate: doc.event.startDate,
    thumbnail: doc.event.thumbnail,
    isEnded: isEventEnded(doc.event.endDate, now),
  };
};

/**
 * @param {object} doc a UserNotifications document from POST /api/notification/list
 * @param {object} options
 * @param {string} options.role the viewer's role, for the Matched destination
 * @param {number} [options.now] epoch ms, injectable so tests are not clock-dependent
 * @returns {object|null} a row descriptor, or null when the document cannot be rendered
 */
export const formatNotificationFeedItem = (doc, { role, now = Date.now() } = {}) => {
  if (!doc?._id || !doc?.content || !doc?.subcategory) {
    return null;
  }

  const route = resolveRoute(doc.subcategory, role);

  // An unrecognised subcategory has no text, icon or destination we can honour.
  // Skipping keeps a future backend category from rendering as a dead row.
  if (!route) {
    return null;
  }

  const isReminder = doc.subcategory === 'Reminder';
  const sender = doc.sender ?? {};
  const fullName = userFullNameBuilder.build(sender.preferredFname, sender.fname, sender.lname);

  return {
    id: String(doc._id),
    subcategory: doc.subcategory,
    title: isReminder ? REMINDER_TITLE : fullName,
    body: resolveBody(doc, fullName),
    iconType: isReminder ? NOTIFICATION_ICON_REMINDER : NOTIFICATION_ICON_AVATAR,
    avatarUrl: sender.profilePic,
    avatarFirstName: sender.preferredFname || sender.fname,
    avatarLastName: sender.lname || sender.preferredLname,
    createdAt: doc.createdAt,
    isRead: doc.isNotificationRead === true,
    route,
    routeState: resolveRouteState(doc.subcategory),
    event: buildEvent(doc, now),
  };
};

/** Maps a page of documents to rows, dropping any that cannot be rendered. */
export const formatNotificationFeed = (docs, options) =>
  (Array.isArray(docs) ? docs : []).map(doc => formatNotificationFeedItem(doc, options)).filter(Boolean);
